import { CSSProperties, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Control, Path, SubmitHandler } from 'react-hook-form';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Button } from '@givebutter/primitives/button';
import * as Combobox from '@givebutter/primitives/combobox';
import { Dot } from '@givebutter/primitives/dot';
import { Label } from '@givebutter/primitives/label';
import { Loader } from '@givebutter/primitives/loader';
import { Separator } from '@givebutter/primitives/separator';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { faChevronRight, faLink } from '@fortawesome/pro-regular-svg-icons';
import { faChevronDown, faInfoCircle, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { getTinymce } from '@tinymce/tinymce-react/lib/cjs/main/ts/TinyMCE';
import {
    QRCodeResponse,
    createAccountQRCode,
    fetchAccountQRCode,
    updateAccountQRCode,
} from 'api/account';
import { aiRequestHandler } from 'api/ai';
import { fetchCampaigns } from 'api/campaigns';
import { createOrUpdatePersonalizedLetter, updateEngageMessageById } from 'api/engage';
import { LetterContentEditorToolbar } from 'assets/js/pages/engage/letters/LetterContentEditorToolbar';
import { LetterFoldPreview } from 'assets/js/pages/engage/letters/LetterFoldPreview';
import {
    transformLetterSettingsToForm,
    transformPageSettingsToRequest,
} from 'assets/js/pages/engage/letters/transform';
import { PageSettingsForm } from 'assets/js/pages/engage/letters/types';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import { useLetterMeta } from 'assets/js/pages/engage/letters/useLetterMeta';
import { usePersonalizedLettersStore } from 'assets/js/pages/engage/letters/usePersonalizedLettersStore';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import * as Tooltip from 'components/tooltip';
import * as Wysiwyg from 'components/wysiwyg';
import { WysiwygContentEditorProps } from 'components/wysiwyg';
import { BASE_PROMPTS, LETTER_PROMPTS } from 'constants/tinyAiPlugin';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteCampaignsByActiveStatus } from 'customHooks/useInfiniteCampaignsByActiveStatus';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { twJoin, twMerge } from 'tailwind-merge';
import { AstNode, Editor } from 'tinymce';
import {
    Campaign,
    Contact,
    EngageLetterType,
    EngageMessage,
    LetterFulfillment,
} from 'types/domain';
import { GivebutterAxiosError } from 'types/error';
import { PaginatedData } from 'types/pagination';

const EDITOR_CONTENT_DEBOUNCE = 500;
const INCH_PX = 64;
const NOTIFICATION_BUFFER_SPACE_PX = 16;
const SAVE_ALERT_DEBOUNCE = 1250;
const SAVE_ALERT_HIDE_INTERVAL = 4000;
const LETTER_CONTENT_AI_PROMPTS = [...BASE_PROMPTS, ...LETTER_PROMPTS];

interface QRCodeFormValues {
    campaignId: string[];
    qrCodeId: number | null;
    contactId: number | null;
    url: string;
    showURL: boolean;
    foregroundColor: string;
    backgroundColor: string;
    isTrackable: boolean;
    callToAction: string;
}

interface LetterContentEditorProps {
    autoSave?: boolean;
    contactId?: number;
    editorProps?: WysiwygContentEditorProps;
}

const LetterContentEditor = (props: LetterContentEditorProps) => {
    const { contactId, autoSave = true, editorProps } = props;
    const { accountId } = useCurrentAccount();
    const message = useToast();
    const { data: letter } = useCurrentLetter();
    const { currentPersonalizedLetterContact, setCurrentPersonalizedLetterContact } =
        usePersonalizedLettersStore(state => ({
            currentPersonalizedLetterContact: state.currentPersonalizedLetterContact,
            setCurrentPersonalizedLetterContact: state.setCurrentPersonalizedLetterContact,
        }));
    const letterId = letter.id;
    const isPersonalizedLetter = !!contactId;
    const hasMissingMergeFields =
        isPersonalizedLetter &&
        currentPersonalizedLetterContact?.mail_piece?.merge_fields_without_value &&
        currentPersonalizedLetterContact?.mail_piece?.merge_fields_without_value.length > 0;
    const isPersonalizedContactRemoved =
        !!currentPersonalizedLetterContact?.mail_piece?.excluded_at;

    const { isEasySend } = useLetterMeta();
    const [showSaveMessage, setShowSaveMessage] = useState(false);
    const [pageSettings, setPageSettings] = useState(transformLetterSettingsToForm(letter));
    const {
        letterDimensions,
        letterMarginTop,
        letterMarginBottom,
        letterMarginLeft,
        letterMarginRight,
    } = pageSettings;
    const {
        handleSubmit: pageSettingsHandleSubmit,
        control: pageSettingsControl,
        path: pageSettingsPath,
    } = Form.useGivebutterForm<PageSettingsForm>({
        defaultValues: {
            letterDimensions: letterDimensions,
            letterMarginLeft: setDefaultMarginIfEmpty(letterMarginLeft),
            letterMarginRight: setDefaultMarginIfEmpty(letterMarginRight),
            letterMarginTop: setDefaultMarginIfEmpty(letterMarginTop),
            letterMarginBottom: setDefaultMarginIfEmpty(letterMarginBottom),
        },
    });

    const qrFormMethods = Form.useGivebutterForm<QRCodeFormValues>({
        defaultValues: {
            campaignId: [],
            qrCodeId: null,
            contactId: null,
            url: '',
            showURL: false,
            backgroundColor: '#ffffff',
            foregroundColor: '#000000',
            isTrackable: isEasySend,
            callToAction: '',
        },
    });
    const {
        handleSubmit: qrCodeHandleSubmit,
        control: qrCodeControl,
        path: qrCodePath,
        watch: qrCodeWatch,
        reset: qrCodeReset,
    } = qrFormMethods;

    const [showURL, campaignId, url, qrCodeId, qrCodeContactId] = qrCodeWatch([
        'showURL',
        'campaignId',
        'url',
        'qrCodeId',
        'contactId',
    ]);
    const isInsertQRCodeDisabled = (!showURL && campaignId === null) || (showURL && url === '');
    const shouldUpdateQRCode =
        (qrCodeId && !isPersonalizedLetter) || (isPersonalizedLetter && qrCodeContactId);
    const initialEditorValue = useRef(
        isPersonalizedLetter
            ? (currentPersonalizedLetterContact?.mail_piece?.body ?? letter.body ?? '')
            : (letter.body ?? ''),
    );
    const [editor, setEditor] = useState<Editor | undefined>(undefined);
    const [showPageSettings, setShowPageSettings] = useState(false);
    const [showQRCodeDialog, setShowQRCodeDialog] = useState(false);
    const editorSaveTimeout = useRef<NodeJS.Timeout>();
    const showSuccessTimeout = useRef<NodeJS.Timeout>();
    const hideSuccessTimeout = useRef<NodeJS.Timeout>();
    const hasFormattedOrSaved = useRef(false);

    const showSaveSuccessNotification = () => {
        if (showSuccessTimeout.current) {
            clearTimeout(showSuccessTimeout.current);
        }

        if (hideSuccessTimeout.current) {
            clearTimeout(hideSuccessTimeout.current);
        }

        showSuccessTimeout.current = setTimeout(
            () => setShowSaveMessage(true),
            SAVE_ALERT_DEBOUNCE,
        );
        hideSuccessTimeout.current = setTimeout(
            () => setShowSaveMessage(false),
            SAVE_ALERT_HIDE_INTERVAL,
        );
    };

    const updateAndInvalidatePersonalizedLetter = useCallback(async () => {
        if (isPersonalizedLetter) {
            const mailPiece = await createOrUpdatePersonalizedLetter(
                accountId,
                letter.id,
                currentPersonalizedLetterContact?.id ?? 0,
                {
                    body: editor?.getContent() ?? '',
                },
            );
            setCurrentPersonalizedLetterContact({
                ...(currentPersonalizedLetterContact as Contact),
                mail_piece: mailPiece ?? null,
            });
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessageById(accountId, letter.id),
            });
        }
    }, [
        accountId,
        letter.id,
        currentPersonalizedLetterContact,
        editor,
        isPersonalizedLetter,
        setCurrentPersonalizedLetterContact,
    ]);

    const { mutate: updateBody } = useMutation<
        LetterFulfillment | EngageMessage,
        GivebutterAxiosError,
        string,
        unknown
    >({
        mutationFn: (body: string) => {
            if (isPersonalizedLetter) {
                return createOrUpdatePersonalizedLetter(accountId, letterId, contactId, { body });
            }

            return updateEngageMessageById(accountId, letterId, { channel: 'letter', body });
        },
        onSuccess: data => {
            showSaveSuccessNotification();
            if (!isPersonalizedLetter) {
                queryClient.setQueryData(
                    accountQueryKeys.engageMessageById(accountId, letterId),
                    data,
                );
            }
        },
    });

    const { mutateAsync: updatePageSettings } = useMutation({
        mutationFn: (values: PageSettingsForm) => {
            const sanitizedValues = {
                letterDimensions: values.letterDimensions,
                letterMarginTop: setDefaultMarginIfEmpty(values.letterMarginTop),
                letterMarginBottom: setDefaultMarginIfEmpty(values.letterMarginBottom),
                letterMarginLeft: setDefaultMarginIfEmpty(values.letterMarginLeft),
                letterMarginRight: setDefaultMarginIfEmpty(values.letterMarginRight),
                postageType: values.postageType,
                isDoubleSided: values.isDoubleSided,
            };

            setPageSettings(prev => ({
                ...prev,
                ...sanitizedValues,
            }));

            return updateEngageMessageById(accountId, letterId, {
                channel: 'letter',
                settings: transformPageSettingsToRequest(sanitizedValues),
            });
        },
        onMutate: () => setShowPageSettings(false),
        onSuccess: () => showSaveSuccessNotification(),
    });

    const { mutate: addQRCode } = useMutation({
        mutationFn: (values: QRCodeFormValues) =>
            createAccountQRCode(accountId, {
                message_id: letterId,
                contact_id: isPersonalizedLetter ? currentPersonalizedLetterContact?.id : undefined,
                ...transformQRFormToRequest(values),
            }),
        onSuccess: data => {
            if (editor) {
                handleQRCodeMutationSuccess(editor, data.qr_code);
            }
        },
        onError: (error: GivebutterAxiosError) => {
            message.error(<>{error.response?.data.message ?? 'Unknown error occurred'}</>);
        },
    });

    const { mutate: updateQRCode } = useMutation({
        mutationFn: (values: QRCodeFormValues) =>
            updateAccountQRCode(accountId, Number(values.qrCodeId), {
                message_id: letterId,
                contact_id: isPersonalizedLetter ? currentPersonalizedLetterContact?.id : undefined,
                ...transformQRFormToRequest(values),
            }),
        onSuccess: data => {
            if (editor) {
                handleQRCodeMutationSuccess(editor, data);
            }
        },
        onError: (error: GivebutterAxiosError) => {
            message.error(<>{error.response?.data.message ?? 'Unknown error occurred'}</>);
        },
    });

    const save = (value: string) => {
        if (editorSaveTimeout.current) {
            clearTimeout(editorSaveTimeout.current);
        }
        editorSaveTimeout.current = setTimeout(() => updateBody(value), EDITOR_CONTENT_DEBOUNCE);
    };

    const handleQRCodeMutationSuccess = (editor: Editor, res: QRCodeResponse['qr_code']) => {
        if (editor) {
            const content = editor.getContent({ format: 'tree' });
            const TinymceInstance = getTinymce(window);
            if (TinymceInstance) {
                const isCta = !!res.call_to_action;
                const img = TinymceInstance?.html.Node.create('img', {
                    id: `qr-code-${res.id}`,
                    alt: 'QR code',
                    src: res.svg ?? '',
                    style: `margin: 0 auto; height: 108px; width: 108px;`,
                });
                const wrapper = TinymceInstance.html.Node.create('div', {
                    id: `qr-code-${res.id}-cta-wrapper`,
                    style: `float: right; padding: 12px; display: flex; flex-direction: column; text-align: center; max-width: 200px; gap: 4px;`,
                    contenteditable: 'false',
                });
                const ctaCaption = TinymceInstance.html.Node.create('div', {
                    style: 'font-size: 14px; font-weight: 600;',
                });
                const ctaText = TinymceInstance.html.Node.create('#text');

                ctaText.value = res.call_to_action ?? '';
                ctaCaption.append(ctaText);
                wrapper.append(img);
                if (isCta) {
                    wrapper.append(ctaCaption);
                }

                walkUntilRemove(content);

                if (content.firstChild) {
                    content.insert(wrapper, content.firstChild, true);
                } else {
                    content.append(wrapper);
                }

                // we serialize here due to a bug with mergetags when directly setting a content "tree"
                const html = TinymceInstance.html.Serializer().serialize(content);
                editor.setContent(html);
            }

            setShowQRCodeDialog(false);
            qrCodeReset();
            updateAndInvalidatePersonalizedLetter();
        }
    };

    useEffect(() => {
        if (editor) {
            editor.focus();
        }
    }, [editor]);

    return (
        <div className={'flex flex-col gap-6'}>
            {hasMissingMergeFields && (
                <div className={'mx-auto w-full max-w-3xl px-4'}>
                    <AlertNotification.Root type={'error'}>
                        <AlertNotification.Icon />
                        <AlertNotification.Content>
                            <AlertNotification.Title>Missing merge fields</AlertNotification.Title>
                            <AlertNotification.Description>
                                This contact is missing a value for these merge fields:{' '}
                                {currentPersonalizedLetterContact.mail_piece?.merge_fields_without_value.join(
                                    ', ',
                                )}
                            </AlertNotification.Description>
                        </AlertNotification.Content>
                    </AlertNotification.Root>
                </div>
            )}
            <LetterContentEditorToolbar />
            <Wysiwyg.WysiwygProvider>
                <div
                    className={twMerge(
                        isPersonalizedLetter && isPersonalizedContactRemoved && 'opacity-50',
                        'h-screen min-h-svh overflow-y-auto pb-96',
                    )}
                >
                    <div className={'mx-auto flex w-full max-w-3xl flex-col gap-4 px-4'}>
                        <Wysiwyg.WysiwygContentContainer
                            className={twJoin(!!editor && 'visible', !editor && 'invisible')}
                        >
                            <Wysiwyg.SavedNotification show={showSaveMessage} />
                            {isEasySend && (
                                <Collapsible.Root
                                    className={
                                        'group flex flex-col gap-4 border-b-2 border-dashed border-gray-300 bg-white px-16 py-1'
                                    }
                                >
                                    <Collapsible.Trigger asChild>
                                        <div className="flex cursor-pointer items-center gap-2 py-3 text-sm">
                                            <span className="flex items-center gap-1.5 font-medium text-blue-500">
                                                <FontAwesomeIcon
                                                    icon={faChevronRight}
                                                    className="group-radix-state-open:rotate-90"
                                                />
                                                <span className="group-radix-state-open:hidden">
                                                    View fold
                                                </span>
                                                <span className="group-radix-state-closed:hidden">
                                                    Hide fold
                                                </span>
                                            </span>
                                            <Tooltip.Root>
                                                <Tooltip.Trigger>
                                                    <FontAwesomeIcon
                                                        icon={faInfoCircle}
                                                        className="text-gray-600"
                                                    />
                                                </Tooltip.Trigger>
                                                <Tooltip.Portal>
                                                    <Tooltip.Content inverse>
                                                        <p>
                                                            Each letter is trifolded, with the
                                                            recipient's address and the
                                                            organization's address appearing in the
                                                            top third of the first page of your
                                                            letter.
                                                        </p>
                                                        <Tooltip.Arrow inverse />
                                                    </Tooltip.Content>
                                                </Tooltip.Portal>
                                            </Tooltip.Root>
                                        </div>
                                    </Collapsible.Trigger>
                                    <Collapsible.Content>
                                        <LetterFoldPreview />
                                    </Collapsible.Content>
                                </Collapsible.Root>
                            )}
                            <Wysiwyg.Margins
                                style={{
                                    paddingTop: valueToPxStyle(
                                        parseFloat(letterMarginTop) * INCH_PX,
                                    ),
                                    paddingBottom: valueToPxStyle(
                                        parseFloat(letterMarginBottom) * INCH_PX,
                                    ),
                                    paddingRight: valueToPxStyle(
                                        parseFloat(letterMarginRight) * INCH_PX,
                                    ),
                                    paddingLeft: valueToPxStyle(
                                        parseFloat(letterMarginLeft) * INCH_PX,
                                    ),
                                    ...getLetterStylesForDimension(letterDimensions),
                                }}
                            >
                                <Wysiwyg.CssReset>
                                    <Wysiwyg.CursorManager
                                        style={{
                                            left: valueToPxStyle(
                                                parseFloat(letterMarginLeft) * INCH_PX +
                                                    NOTIFICATION_BUFFER_SPACE_PX,
                                            ),
                                            right: valueToPxStyle(
                                                parseFloat(letterMarginRight) * INCH_PX +
                                                    NOTIFICATION_BUFFER_SPACE_PX,
                                            ),
                                            bottom: valueToPxStyle(
                                                parseFloat(letterMarginBottom) * INCH_PX,
                                            ),
                                        }}
                                    />
                                    <Wysiwyg.Editor
                                        onInit={(_, editor) => {
                                            setTimeout(() => {
                                                setEditor(editor);
                                                editor.setContent(initialEditorValue.current, {
                                                    content: initialEditorValue.current,
                                                    initial: true,
                                                });
                                                editor.undoManager.clear();

                                                if (!isPersonalizedLetter) {
                                                    editor.setDirty(true);
                                                }

                                                if (
                                                    isPersonalizedLetter &&
                                                    isPersonalizedContactRemoved
                                                ) {
                                                    editor.mode.set('readonly');
                                                }

                                                // Workaround for the TinyMCE bug where the first format applied
                                                // does not fire the onEditorChange event
                                                editor.on('FormatApply', () => {
                                                    if (
                                                        autoSave &&
                                                        editor.isDirty() &&
                                                        !hasFormattedOrSaved.current
                                                    ) {
                                                        hasFormattedOrSaved.current = true;
                                                        save(editor.getContent());
                                                    }
                                                });

                                                editorProps?.onInit?.(_, editor);
                                            }, 0);
                                        }}
                                        onEditorChange={(value, editor) => {
                                            if (autoSave && editor.isDirty()) {
                                                save(value);
                                                if (!hasFormattedOrSaved.current) {
                                                    hasFormattedOrSaved.current = true;
                                                }
                                            }
                                            editorProps?.onEditorChange?.(value, editor);
                                        }}
                                        init={{
                                            placeholder:
                                                editorProps?.init?.placeholder ??
                                                'Write your letter content here...',
                                            toolbar_mode: 'floating',
                                            newline_behavior: 'block',
                                            setup: editor => {
                                                if (!isPersonalizedLetter) {
                                                    editor.addShortcut(
                                                        'meta+s',
                                                        'Save your content.',
                                                        () => {
                                                            save(editor.getContent());
                                                        },
                                                    );
                                                }

                                                if (!isEasySend) {
                                                    editor.ui.registry.addToggleMenuItem(
                                                        'pageSettings',
                                                        {
                                                            icon: 'settings',
                                                            text: 'Page settings',
                                                            onAction: api => {
                                                                const isActive = !api.isActive();
                                                                api.setActive(isActive);
                                                                setShowPageSettings(isActive);
                                                            },
                                                        },
                                                    );
                                                }
                                                editor.ui.registry.addIcon(
                                                    'qrCode',
                                                    '<svg xmlns="http://www.w3.org/2000/svg" height="16" width="14" viewBox="0 0 448 512"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M0 80C0 53.5 21.5 32 48 32h96c26.5 0 48 21.5 48 48v96c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V80zM64 96v64h64V96H64zM0 336c0-26.5 21.5-48 48-48h96c26.5 0 48 21.5 48 48v96c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V336zm64 16v64h64V352H64zM304 32h96c26.5 0 48 21.5 48 48v96c0 26.5-21.5 48-48 48H304c-26.5 0-48-21.5-48-48V80c0-26.5 21.5-48 48-48zm80 64H320v64h64V96zM256 304c0-8.8 7.2-16 16-16h64c8.8 0 16 7.2 16 16s7.2 16 16 16h32c8.8 0 16-7.2 16-16s7.2-16 16-16s16 7.2 16 16v96c0 8.8-7.2 16-16 16H368c-8.8 0-16-7.2-16-16s-7.2-16-16-16s-16 7.2-16 16v64c0 8.8-7.2 16-16 16H272c-8.8 0-16-7.2-16-16V304zM368 480a16 16 0 1 1 0-32 16 16 0 1 1 0 32zm64 0a16 16 0 1 1 0-32 16 16 0 1 1 0 32z"/></svg>',
                                                );
                                                editor.ui.registry.addIcon(
                                                    'faEdit',
                                                    '<svg xmlns="http://www.w3.org/2000/svg" height="16" width="14" viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M471.6 21.7c-21.9-21.9-57.3-21.9-79.2 0L362.3 51.7l97.9 97.9 30.1-30.1c21.9-21.9 21.9-57.3 0-79.2L471.6 21.7zm-299.2 220c-6.1 6.1-10.8 13.6-13.5 21.9l-29.6 88.8c-2.9 8.6-.6 18.1 5.8 24.6s15.9 8.7 24.6 5.8l88.8-29.6c8.2-2.7 15.7-7.4 21.9-13.5L437.7 172.3 339.7 74.3 172.4 241.7zM96 64C43 64 0 107 0 160V416c0 53 43 96 96 96H352c53 0 96-43 96-96V320c0-17.7-14.3-32-32-32s-32 14.3-32 32v96c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V160c0-17.7 14.3-32 32-32h96c17.7 0 32-14.3 32-32s-14.3-32-32-32H96z"/></svg>',
                                                );
                                                if (isEasySend) {
                                                    editor.ui.registry.addButton('qrCode', {
                                                        tooltip: 'Insert QR code',
                                                        icon: 'qrCode',
                                                        onAction: api => {
                                                            setShowQRCodeDialog(api.isEnabled());
                                                        },
                                                    });
                                                    editor.ui.registry.addToggleMenuItem('qrCode', {
                                                        icon: 'qrCode',
                                                        text: 'QR Code',
                                                        onAction: api => {
                                                            const isActive = !api.isActive();
                                                            api.setActive(isActive);
                                                            setShowQRCodeDialog(isActive);
                                                        },
                                                    });
                                                    editor.ui.registry.addButton('editQRCode', {
                                                        tooltip: 'Edit QR code',
                                                        icon: 'faEdit',
                                                        onAction: async api => {
                                                            const nodeID =
                                                                editor.selection.getNode().id;
                                                            if (nodeID.includes('qr-code')) {
                                                                const qrCodeIDValue =
                                                                    nodeID.replace(
                                                                        /^qr-code-([^-]+)(?:-cta-wrapper)?$/,
                                                                        '$1',
                                                                    );
                                                                const qrCodeResponse =
                                                                    await fetchAccountQRCode(
                                                                        accountId,
                                                                        Number(qrCodeIDValue),
                                                                    );

                                                                qrCodeReset(
                                                                    {
                                                                        url:
                                                                            qrCodeResponse.custom_url ??
                                                                            '',
                                                                        showURL:
                                                                            qrCodeResponse.custom_url !==
                                                                            null,
                                                                        callToAction:
                                                                            qrCodeResponse.call_to_action ??
                                                                            '',
                                                                        foregroundColor:
                                                                            qrCodeResponse.color,
                                                                        backgroundColor:
                                                                            qrCodeResponse.background_color,
                                                                        isTrackable:
                                                                            qrCodeResponse.is_trackable,
                                                                        qrCodeId: qrCodeResponse.id,
                                                                        contactId:
                                                                            qrCodeResponse.contact_id,
                                                                        campaignId:
                                                                            qrCodeResponse.campaign_id !==
                                                                                null &&
                                                                            qrCodeResponse.campaign_title !==
                                                                                null
                                                                                ? [
                                                                                      qrCodeResponse.campaign_id.toString(),
                                                                                  ]
                                                                                : [],
                                                                    },
                                                                    { keepDefaultValues: true },
                                                                );
                                                            }
                                                            setShowQRCodeDialog(api.isEnabled());
                                                        },
                                                    });
                                                }

                                                editorProps?.init?.setup?.(editor);
                                            },
                                            image_description: false,
                                            images_file_types: 'jpg,jpeg,png',
                                            ai_request: aiRequestHandler(
                                                accountId,
                                                LETTER_CONTENT_AI_PROMPTS,
                                            ),
                                            ...editorProps?.init,
                                        }}
                                    />
                                </Wysiwyg.CssReset>
                            </Wysiwyg.Margins>
                        </Wysiwyg.WysiwygContentContainer>
                        {!isPersonalizedLetter && (
                            <Dialog.Root
                                open={showPageSettings}
                                onOpenChange={value => {
                                    setShowPageSettings(value);
                                }}
                            >
                                <Dialog.Portal>
                                    <Dialog.Overlay />
                                    <Dialog.Content>
                                        <Dialog.Close />
                                        <form
                                            onSubmit={e => {
                                                e.preventDefault();
                                                e.stopPropagation();

                                                return pageSettingsHandleSubmit(
                                                    updatePageSettings as SubmitHandler<PageSettingsForm>,
                                                )(e);
                                            }}
                                        >
                                            <div className={'flex flex-col gap-6'}>
                                                <Dialog.Title>Page settings</Dialog.Title>
                                                <Form.Container>
                                                    <Form.Label
                                                        name={pageSettingsPath('letterDimensions')}
                                                    >
                                                        Dimensions
                                                    </Form.Label>
                                                    <Form.SingleSelect
                                                        control={pageSettingsControl}
                                                        name="letterDimensions"
                                                    >
                                                        <SingleSelect.Trigger aria-label="Letter dimensions" />
                                                        <SingleSelect.Content>
                                                            <SingleSelect.Item value="letter">
                                                                <SingleSelect.ItemText>
                                                                    Letter (8.5" x 11")
                                                                </SingleSelect.ItemText>
                                                            </SingleSelect.Item>
                                                            <SingleSelect.Item value="half_letter">
                                                                <SingleSelect.ItemText>
                                                                    Half-letter (5.5" x 8.5")
                                                                </SingleSelect.ItemText>
                                                            </SingleSelect.Item>
                                                            <SingleSelect.Item value="legal">
                                                                <SingleSelect.ItemText>
                                                                    Legal (8.5" x 14")
                                                                </SingleSelect.ItemText>
                                                            </SingleSelect.Item>
                                                            <SingleSelect.Item value="a4">
                                                                <SingleSelect.ItemText>
                                                                    A4 size (210 x 297 mm)
                                                                </SingleSelect.ItemText>
                                                            </SingleSelect.Item>
                                                        </SingleSelect.Content>
                                                    </Form.SingleSelect>
                                                </Form.Container>
                                                <Form.Container>
                                                    <Label>Margin (inches)</Label>
                                                    <div className={'grid grid-cols-2 gap-4'}>
                                                        <Form.Container>
                                                            <Form.TextInputRoot
                                                                control={pageSettingsControl}
                                                                name={'letterMarginTop'}
                                                                inputRootProps={{
                                                                    mask: 'decimal',
                                                                }}
                                                            >
                                                                <Form.TextInputForm />
                                                            </Form.TextInputRoot>
                                                            <Form.Label
                                                                name={pageSettingsPath(
                                                                    'letterMarginTop',
                                                                )}
                                                            >
                                                                Top
                                                            </Form.Label>
                                                        </Form.Container>
                                                        <Form.Container>
                                                            <Form.TextInputRoot
                                                                control={pageSettingsControl}
                                                                name={'letterMarginBottom'}
                                                                inputRootProps={{
                                                                    mask: 'decimal',
                                                                }}
                                                            >
                                                                <Form.TextInputForm />
                                                            </Form.TextInputRoot>
                                                            <Form.Label
                                                                name={pageSettingsPath(
                                                                    'letterMarginBottom',
                                                                )}
                                                            >
                                                                Bottom
                                                            </Form.Label>
                                                        </Form.Container>
                                                        <Form.Container>
                                                            <Form.TextInputRoot
                                                                control={pageSettingsControl}
                                                                name={'letterMarginLeft'}
                                                                inputRootProps={{
                                                                    mask: 'decimal',
                                                                }}
                                                            >
                                                                <Form.TextInputForm />
                                                            </Form.TextInputRoot>
                                                            <Form.Label
                                                                name={pageSettingsPath(
                                                                    'letterMarginLeft',
                                                                )}
                                                            >
                                                                Left
                                                            </Form.Label>
                                                        </Form.Container>
                                                        <Form.Container>
                                                            <Form.TextInputRoot
                                                                control={pageSettingsControl}
                                                                name={'letterMarginRight'}
                                                                inputRootProps={{
                                                                    mask: 'decimal',
                                                                }}
                                                            >
                                                                <Form.TextInputForm />
                                                            </Form.TextInputRoot>
                                                            <Form.Label
                                                                name={pageSettingsPath(
                                                                    'letterMarginRight',
                                                                )}
                                                            >
                                                                Right
                                                            </Form.Label>
                                                        </Form.Container>
                                                    </div>
                                                </Form.Container>
                                                <div className={'flex justify-between gap-4'}>
                                                    <Button
                                                        variant={'outline'}
                                                        color={'neutral'}
                                                        onClick={() => {
                                                            setShowPageSettings(false);
                                                        }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button variant="solid" type={'submit'}>
                                                        Update
                                                    </Button>
                                                </div>
                                            </div>
                                        </form>
                                    </Dialog.Content>
                                </Dialog.Portal>
                            </Dialog.Root>
                        )}
                        <Dialog.Root
                            open={showQRCodeDialog}
                            onOpenChange={open => {
                                setShowQRCodeDialog(open);
                                if (!open) {
                                    qrCodeReset();
                                }
                            }}
                        >
                            <Dialog.Portal>
                                <Dialog.Overlay />
                                <Dialog.Content>
                                    <Dialog.Close />
                                    <Form.Provider {...qrFormMethods}>
                                        <form
                                            onSubmit={e => {
                                                e.preventDefault();
                                                e.stopPropagation();

                                                return qrCodeHandleSubmit(
                                                    (shouldUpdateQRCode
                                                        ? updateQRCode
                                                        : addQRCode) as SubmitHandler<QRCodeFormValues>,
                                                )(e);
                                            }}
                                        >
                                            <div className={'flex flex-col gap-6'}>
                                                <div>
                                                    <Dialog.Title className="!text-base">
                                                        QR Code
                                                    </Dialog.Title>
                                                    <div className="text-sm text-gray-600">
                                                        Create a QR code that directs to a
                                                        campaign's donation form or URL, allowing
                                                        recipients to easily access the link.
                                                    </div>
                                                </div>
                                                <div className="flex flex-col gap-4">
                                                    <Form.Container>
                                                        <Label>Campaign</Label>
                                                        <Suspense fallback={<Loader />}>
                                                            <QrCodeCampaignSelect
                                                                control={qrCodeControl}
                                                                name={qrCodePath('campaignId')}
                                                            />
                                                        </Suspense>
                                                        <Form.Error
                                                            name={qrCodePath('campaignId')}
                                                        />
                                                    </Form.Container>

                                                    <div className="flex flex-col gap-1.5">
                                                        <Form.Container className="flex flex-row items-center gap-2">
                                                            <Form.UseFormCheckboxInput
                                                                control={qrCodeControl}
                                                                name={qrCodePath('showURL')}
                                                            />
                                                            <Form.Label
                                                                name={qrCodePath('showURL')}
                                                            >
                                                                Enter custom URL
                                                            </Form.Label>
                                                        </Form.Container>
                                                        {showURL && (
                                                            <Form.Container>
                                                                <Form.TextInputRoot
                                                                    control={qrCodeControl}
                                                                    name={qrCodePath('url')}
                                                                >
                                                                    <Form.TextInputForm placeholder="Insert URL" />
                                                                    <FontAwesomeIcon
                                                                        icon={faLink}
                                                                        className="text-gray-400"
                                                                    />
                                                                </Form.TextInputRoot>
                                                                <Form.Error
                                                                    name={qrCodePath('url')}
                                                                />
                                                            </Form.Container>
                                                        )}
                                                    </div>
                                                    <Form.Container>
                                                        <Form.Label
                                                            name={qrCodePath('foregroundColor')}
                                                        >
                                                            QR code color
                                                        </Form.Label>
                                                        <Form.UseFormColorPicker
                                                            className="w-full"
                                                            control={qrCodeControl}
                                                            name={qrCodePath('foregroundColor')}
                                                        />
                                                        <Form.Error
                                                            name={qrCodePath('foregroundColor')}
                                                        />
                                                    </Form.Container>
                                                    <Form.Container>
                                                        <Form.Label
                                                            name={qrCodePath('backgroundColor')}
                                                        >
                                                            Background color
                                                        </Form.Label>
                                                        <Form.UseFormColorPicker
                                                            className="w-full"
                                                            control={qrCodeControl}
                                                            name={qrCodePath('backgroundColor')}
                                                        />
                                                        <Form.Error
                                                            name={qrCodePath('backgroundColor')}
                                                        />
                                                    </Form.Container>
                                                    <Form.Container>
                                                        <Form.Label
                                                            name={qrCodePath('callToAction')}
                                                        >
                                                            Call to action (optional)
                                                        </Form.Label>
                                                        <Form.TextInputRoot
                                                            control={qrCodeControl}
                                                            name={qrCodePath('callToAction')}
                                                        >
                                                            <Form.TextInputForm placeholder="Scan to Donate" />
                                                        </Form.TextInputRoot>
                                                        <Form.Error
                                                            name={qrCodePath('callToAction')}
                                                        />
                                                    </Form.Container>
                                                </div>
                                                <div className={'flex justify-end gap-4'}>
                                                    {(isEasySend || qrCodeId) && (
                                                        <Button
                                                            variant="solid"
                                                            type={'submit'}
                                                            disabled={isInsertQRCodeDisabled}
                                                        >
                                                            {qrCodeId ? 'Update' : 'Insert'} QR code
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </form>
                                    </Form.Provider>
                                </Dialog.Content>
                            </Dialog.Portal>
                        </Dialog.Root>
                    </div>
                </div>
            </Wysiwyg.WysiwygProvider>
        </div>
    );
};

const setDefaultMarginIfEmpty = (str = '') => (str.length === 0 ? '1' : str);

const getLetterStylesForDimension = (dimensions: EngageLetterType): CSSProperties => {
    switch (dimensions) {
        case 'letter':
            return { width: '100%', minHeight: '100vh' };
        case 'half_letter':
            return { width: '60%', margin: 'auto', minHeight: '50vh' };
        case 'legal':
            return { width: '100%', minHeight: '150vh' };
        case 'a4':
            return { width: '100%', minHeight: '125vh' };
    }
};

const valueToPxStyle = <T,>(value: T) => {
    return `${value}px`;
};

const transformQRFormToRequest = (values: QRCodeFormValues) => {
    return {
        is_trackable: values.isTrackable ?? false,
        campaign_id:
            !values.showURL && values.campaignId ? Number(values.campaignId[0]) : undefined,
        custom_url: values.showURL && values.url ? values.url : undefined,
        color: values.foregroundColor,
        background_color: values.backgroundColor,
        call_to_action: values.callToAction,
    };
};

function walkUntilRemove(node?: AstNode | null) {
    if (!node) {
        return null;
    }

    if (node.attr('id')?.includes('qr-code')) {
        node.remove();
    }

    return node.children().forEach(walkUntilRemove);
}

interface QrCodeCampaignSelectProps {
    control: Control<QRCodeFormValues>;
    name: Path<QRCodeFormValues>;
}
const QrCodeCampaignSelect = ({ control, name }: QrCodeCampaignSelectProps) => {
    const { accountId } = useCurrentAccount();
    const [searchCampaigns, setSearchCampaigns] = useState('');
    const debouncedSearchCampaigns = useDebounce(searchCampaigns);
    const { data: campaignsData, ref: campaignsRef } = useInfiniteCampaignsByActiveStatus({
        query: debouncedSearchCampaigns,
        sortBy: 'active',
    });

    const campaigns = campaignsData?.pages ?? [];
    const methods = Form.useFormContext<QRCodeFormValues>();
    const { watch, setValue } = methods;
    const [campaignId, showURL] = watch(['campaignId', 'showURL']);

    const params = {
        ids: campaignId.map(Number),
        page: 1,
        per_page: campaignId.length,
    };

    const { data: savedQrCampaigns } = useSuspenseQuery({
        queryKey: accountQueryKeys.campaignsWithFilters(accountId, params),
        queryFn: () => {
            if (campaignId.length === 0) {
                return Promise.resolve({
                    data: [],
                    links: {} as any,
                    meta: {} as any,
                } as PaginatedData<Campaign>);
            }

            return fetchCampaigns(accountId, params);
        },
    });

    return (
        <Form.Combobox
            disabled={showURL}
            control={control}
            name={name}
            allowMultiple={false}
            onChangeValue={() => setSearchCampaigns('')}
        >
            <Combobox.Trigger>
                <Combobox.Select>
                    <Combobox.SelectValue placeholder="Search or select a campaign" />
                    <div className="flex items-center gap-2">
                        {campaignId.length > 0 ? (
                            <Button
                                variant="none"
                                onClick={() => setValue('campaignId', [])}
                                aria-label="clear selection"
                            >
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    className="size-3.5 text-gray-500"
                                />
                            </Button>
                        ) : (
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className="size-3.5 text-gray-500"
                            />
                        )}
                    </div>
                </Combobox.Select>
            </Combobox.Trigger>
            <Combobox.Popover>
                <Combobox.Search
                    value={searchCampaigns}
                    onChangeValue={setSearchCampaigns}
                    placeholder="Search campaigns"
                />
                <Combobox.Items>
                    {savedQrCampaigns.data.length > 0 && (
                        <>
                            {savedQrCampaigns.data.map(campaign => (
                                <QrCodeCampaignComboboxItem key={campaign.id} campaign={campaign} />
                            ))}
                            <Separator />
                        </>
                    )}
                    {campaigns.map(campaign => {
                        if (savedQrCampaigns.data.some(c => c.id === campaign.id)) {
                            return null;
                        }
                        return <QrCodeCampaignComboboxItem key={campaign.id} campaign={campaign} />;
                    })}
                    <div ref={campaignsRef} />
                </Combobox.Items>
            </Combobox.Popover>
        </Form.Combobox>
    );
};

interface QrCodeCampaignComboboxItemProps {
    campaign: Campaign;
}
const QrCodeCampaignComboboxItem = ({ campaign }: QrCodeCampaignComboboxItemProps) => {
    return (
        <Combobox.Item key={campaign.id} value={campaign.id.toString()}>
            <Dot className={campaign.active ? 'bg-green-500' : 'bg-red-500'} />
            <Combobox.ItemText>
                <span className="text-sm font-medium">{campaign.title}</span>
            </Combobox.ItemText>
            <Combobox.CheckmarkIndicator />
        </Combobox.Item>
    );
};

export { LetterContentEditor };
